import '../recruit/common/Recruit.scss'
import './Apply.scss'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import axios from 'axios'
import MainURL from '../../MainURL'

interface TimelineItem {
  status: string
  label: string
  at: string
  memo: string | null
}

interface GuestApplication {
  id: number
  status: string
  statusLabel: string
  version: number
  appliedAt: string
  viewedAt: string | null
  relaySentAt: string | null
  relayTo: string
  relayFailed: boolean
  /** 가져온 공고의 예약 지원 — HELD(교회 확인 대기) · DELIVERED · EXPIRED_NOTICE(원문 직접 지원 안내) · WITHDRAWN */
  deliveryState: string | null
  originalUrl: string | null
  churchMemo: string | null
  interviewAt: string | null
  name: string
  files: string[]
  job: { title: string; church: string; region: string; path: string | null; deleted: boolean; closed: boolean }
  timeline: TimelineItem[]
  canWithdraw: boolean
}

// 비회원 파일 지원자의 진행 상태 (/apply/status/:token — 지원 확인 메일의 링크)
export default function GuestApplyStatus() {
  const { token } = useParams()
  const [app, setApp] = useState<GuestApplication | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => {
    axios.get(`${MainURL}/applyrelay/guest/${token}`)
      .then(res => setApp(res.data.application))
      .catch(err => setError(err.response?.data?.error || '지원 내역을 불러오지 못했습니다.'))
  }

  useEffect(load, [token])

  const withdraw = async () => {
    const message = app?.deliveryState === 'HELD'
      ? '이 관심 표시를 취소할까요? 아직 교회에 전달되지 않았습니다.'
      : '이 지원을 취소할까요? 교회에는 이미 새 지원자 알림이 갔을 수 있습니다.'
    if (!app || busy || !window.confirm(message)) return
    setBusy(true)
    try {
      await axios.post(`${MainURL}/applyrelay/guest/${token}/withdraw`, { version: app.version })
      load()
    } catch (err: any) {
      alert(err.response?.data?.error || '지원 취소에 실패했습니다.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="recruit">
      <div className="inner">
        <div className="subpage__main apply-page">
          {error && <p className="apply-page__empty">{error}</p>}
          {!error && !app && <p className="apply-page__empty">불러오는 중...</p>}
          {app && (
            <>
              <div className="apply-page__head">
                <span className={`apply-status apply-status--${app.status}`}>{app.statusLabel}</span>
                <h3 className="main_title">{app.job.church}</h3>
                <p className="apply-page__lead">
                  {app.job.path && !app.job.deleted ? <Link to={app.job.path}>{app.job.title}</Link> : app.job.title}
                  {app.job.region ? ` · ${app.job.region}` : ''}
                </p>
              </div>

              <div className="apply-card">
                <div className="apply-card__row"><span>지원자</span><b>{app.name}</b></div>
                <div className="apply-card__row"><span>지원일</span><b>{app.appliedAt}</b></div>
                <div className="apply-card__row">
                  <span>교회 알림</span>
                  <b>
                    {app.deliveryState === 'HELD'
                      ? '교회가 아직 사역자모아에서 공고를 확인하지 않았습니다. 확인하면 바로 전달됩니다.'
                      : app.deliveryState === 'EXPIRED_NOTICE'
                        ? '교회가 30일 안에 확인하지 않아 전달되지 않았습니다.'
                        : app.deliveryState === 'WITHDRAWN'
                          ? '공고가 내려가 전달되지 않았습니다.'
                          : app.deliveryState === 'DELIVERED' && !app.relaySentAt
                            ? '교회가 공고를 인수해 사역자모아에서 볼 수 있게 되었습니다.'
                            : app.relaySentAt
                              ? `${app.relaySentAt} · 담당자(${app.relayTo})에게 새 지원자 알림을 보냈습니다`
                              : app.relayFailed ? '알림 메일을 보내지 못해 운영팀이 확인 중입니다. 지원서는 접수되어 있습니다.' : '알림 보내는 중'}
                  </b>
                </div>
                <div className="apply-card__row"><span>교회 확인</span><b>{app.viewedAt ? `${app.viewedAt} 사역자모아에서 확인` : '사역자모아에서는 아직 확인 기록이 없습니다'}</b></div>
                {app.interviewAt && <div className="apply-card__row"><span>면접</span><b>{app.interviewAt}</b></div>}
                {app.churchMemo && <div className="apply-card__row"><span>교회 안내</span><b>{app.churchMemo}</b></div>}
                <div className="apply-card__row"><span>보낸 파일</span><b>{app.files.join(', ')}</b></div>
              </div>

              <h4 className="apply-page__sub">진행 기록</h4>
              <ol className="apply-timeline">
                {app.timeline.map((t, i) => (
                  <li key={`${t.status}-${i}`}>
                    <b>{t.label}</b>
                    <span>{t.at}</span>
                    {t.memo && <p>{t.memo}</p>}
                  </li>
                ))}
              </ol>

              {app.deliveryState === 'EXPIRED_NOTICE' && app.originalUrl && (
                <div className="apply-page__note">
                  아직 모집 중일 수 있습니다. <a href={app.originalUrl} target="_blank" rel="noopener noreferrer">원문 게시글</a>의 안내대로 직접 지원해 주세요.
                </div>
              )}

              <div className="apply-page__note">
                교회의 회신은 지원할 때 입력한 이메일로 직접 옵니다. 사역자모아는 교회의 메일함을 볼 수 없어 교회가 지원서를 읽었는지는 약속드릴 수 없고,
                교회가 사역자모아에서 연락·면접·청빙 제안 등으로 상태를 바꾼 경우에만 여기에 기록되고 이메일로 알려 드립니다.
              </div>

              <div className="apply-page__actions">
                <Link to="/login" className="apply-btn apply-btn--primary">카카오로 시작하고 지원 내역 모으기</Link>
                {app.canWithdraw && (
                  <button type="button" className="apply-btn" onClick={withdraw} disabled={busy}>지원 취소</button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
